import React, { useEffect, useRef, useState } from 'react';
import { Event } from '../types';
import { getDisplayCoordinates, getPrivacyRadiusMeters, getRadiusCircleStyle, getEventMapZoom, getMultiEventMapBounds } from '../utils/mapPrivacy';
import { Plus, Minus } from 'lucide-react';
import { getGoogleMapsLoader } from '../utils/googleMapsLoader';

interface MapViewProps {
  events: Event[];
  selectedEvent?: Event | null;
  userLocation?: { lat: number; lng: number };
  currentUserId?: string;
  onEventSelect?: (event: Event) => void;
  className?: string;
  interactive?: boolean;
  showUserLocation?: boolean;
  acceptedEventIds?: string[];
  mapContext?: 'explore' | 'detail'; // Context for radius sizing
  showRadiusCircles?: boolean; // Whether to show radius circles
}

const MapView: React.FC<MapViewProps> = ({
  events,
  selectedEvent = null,
  userLocation,
  currentUserId,
  onEventSelect,
  className = '',
  interactive = true,
  showUserLocation = true,
  acceptedEventIds = [],
  mapContext = 'detail', // Default to detail view (larger radius)
  showRadiusCircles = true, // Default to showing radius circles
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const circlesRef = useRef<Map<string, google.maps.Circle>>(new Map());
  const userMarkerRef = useRef<google.maps.Marker | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Store the callback in a ref to avoid triggering marker recreation
  const onEventSelectRef = useRef(onEventSelect);
  useEffect(() => {
    onEventSelectRef.current = onEventSelect;
  }, [onEventSelect]);

  useEffect(() => {
    const initializeMap = async () => {
      if (!mapRef.current) return;

      try {
        await getGoogleMapsLoader();
        setIsLoaded(true);
        setLoadError(null);

        // Determine initial center and zoom
        let mapCenter: { lat: number; lng: number };
        let mapZoom: number;

        if (selectedEvent) {
          // Single event mode - center on event
          const isOwner = currentUserId === selectedEvent.posterId;
          const isAccepted = acceptedEventIds.includes(selectedEvent.id);
          const displayCoords = getDisplayCoordinates(
            { id: selectedEvent.id, latitude: selectedEvent.latitude, longitude: selectedEvent.longitude, venueType: selectedEvent.venueType },
            isOwner,
            isAccepted
          );
          mapCenter = displayCoords;
          mapZoom = getEventMapZoom(selectedEvent.venueType);
        } else {
          // Default to user location or fallback to Maidstone
          mapCenter = userLocation || { lat: 51.2727, lng: 0.5199 };
          mapZoom = 13;
        }

        const map = new google.maps.Map(mapRef.current, {
          center: mapCenter,
          zoom: mapZoom,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }],
            },
          ],
          disableDefaultUI: true,
          zoomControl: false,
          scrollwheel: interactive,
          draggable: interactive,
          gestureHandling: interactive ? 'auto' : 'none',
        });

        mapInstanceRef.current = map;

      } catch (error) {
        console.error('Error loading Google Maps:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load map';
        setLoadError(errorMessage);
      }
    };

    initializeMap();

    return () => {
      // Cleanup markers and circles
      markersRef.current.forEach(marker => marker.setMap(null));
      circlesRef.current.forEach(circle => circle.setMap(null));
      if (userMarkerRef.current) {
        userMarkerRef.current.setMap(null);
      }
    };
  }, []);

  // Update markers when events change
  useEffect(() => {
    if (!mapInstanceRef.current || !isLoaded) return;

    // Create a set of current event IDs
    const currentEventIds = new Set(events.map(e => e.id));

    // Remove markers that no longer exist in events
    markersRef.current.forEach((marker, eventId) => {
      if (!currentEventIds.has(eventId)) {
        marker.setMap(null);
        markersRef.current.delete(eventId);
      }
    });

    circlesRef.current.forEach((circle, eventId) => {
      if (!currentEventIds.has(eventId)) {
        circle.setMap(null);
        circlesRef.current.delete(eventId);
      }
    });

    // Add or update markers for each event
    events.forEach(event => {
      // Skip events without coordinates
      if (!event.latitude || !event.longitude) {
        console.warn('Event missing coordinates:', event.id, event.title);
        return;
      }

      const isOwner = currentUserId === event.posterId;
      const isAccepted = acceptedEventIds.includes(event.id);
      const displayCoords = getDisplayCoordinates(
        { id: event.id, latitude: event.latitude, longitude: event.longitude, venueType: event.venueType },
        isOwner,
        isAccepted
      );

      // Check if marker already exists
      const existingMarker = markersRef.current.get(event.id);
      const existingCircle = circlesRef.current.get(event.id);

      if (existingMarker) {
        // Update existing marker position and zIndex
        existingMarker.setPosition(displayCoords);
        existingMarker.setZIndex(selectedEvent?.id === event.id ? 1000 : 100);
        if (existingCircle) {
          existingCircle.setCenter(displayCoords);
        }
      } else {
        // Create new marker
        const marker = new google.maps.Marker({
          position: displayCoords,
          map: mapInstanceRef.current,
          title: event.title,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#FF3B9A',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
          zIndex: selectedEvent?.id === event.id ? 1000 : 100,
          clickable: true,
        });

        // Add radius circle (if enabled)
        let circle: google.maps.Circle | null = null;
        if (showRadiusCircles) {
          const radiusStyle = getRadiusCircleStyle();
          circle = new google.maps.Circle({
            center: displayCoords,
            radius: getPrivacyRadiusMeters(mapContext),
            map: mapInstanceRef.current,
            ...radiusStyle,
            clickable: false,
          });
        }

        // Add click handler
        if (interactive && onEventSelectRef.current) {
          marker.addListener('click', () => {
            console.log('Marker clicked:', event.title);
            try {
              onEventSelectRef.current?.(event);
            } catch (error) {
              console.error('Error in onEventSelect:', error);
            }
          });
        }

        markersRef.current.set(event.id, marker);
        if (circle) {
          circlesRef.current.set(event.id, circle);
        }
      }
    });

    // Add user location marker
    if (showUserLocation && userLocation && mapInstanceRef.current) {
      if (userMarkerRef.current) {
        userMarkerRef.current.setPosition(userLocation);
      } else {
        userMarkerRef.current = new google.maps.Marker({
          position: userLocation,
          map: mapInstanceRef.current,
          title: 'Your location',
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: '#3b82f6', // Blue
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
          zIndex: 500,
        });
      }
    }

  }, [events, selectedEvent, userLocation, currentUserId, acceptedEventIds, isLoaded, interactive, showUserLocation]);

  const handleZoomIn = () => {
    if (mapInstanceRef.current) {
      const currentZoom = mapInstanceRef.current.getZoom() || 13;
      mapInstanceRef.current.setZoom(currentZoom + 1);
    }
  };

  const handleZoomOut = () => {
    if (mapInstanceRef.current) {
      const currentZoom = mapInstanceRef.current.getZoom() || 13;
      mapInstanceRef.current.setZoom(currentZoom - 1);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div
        ref={mapRef}
        className="w-full h-full rounded-lg border border-theme-border-primary"
        style={{ minHeight: '400px' }}
      />
      {!isLoaded && !loadError && (
        <div className="absolute inset-0 flex items-center justify-center bg-theme-bg-primary rounded-lg">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-secondary"></div>
            <p className="text-sm text-theme-text-secondary">Loading map...</p>
          </div>
        </div>
      )}
      {loadError && (
        <div className="absolute inset-0 flex items-center justify-center bg-theme-bg-primary rounded-lg border border-theme-border-primary">
          <div className="flex flex-col items-center gap-3 max-w-md text-center p-6">
            <div className="text-red-500 bg-red-500/10 rounded-full p-3">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-theme-text-primary mb-2">Map Unavailable</h3>
              <p className="text-sm text-theme-text-secondary mb-4">{loadError}</p>
              {loadError.includes('API key') && (
                <a
                  href="https://console.cloud.google.com/google/maps-apis"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 transition-colors text-sm"
                >
                  Get API Key
                </a>
              )}
            </div>
          </div>
        </div>
      )}
      {interactive && isLoaded && (
        <div className="absolute right-4 top-4 flex flex-col gap-2">
          <button
            onClick={handleZoomIn}
            className="bg-white hover:bg-gray-50 border-2 border-gray-300 rounded-lg p-3 shadow-xl transition-all hover:shadow-2xl"
            aria-label="Zoom in"
          >
            <Plus className="w-6 h-6 text-gray-900" />
          </button>
          <button
            onClick={handleZoomOut}
            className="bg-white hover:bg-gray-50 border-2 border-gray-300 rounded-lg p-3 shadow-xl transition-all hover:shadow-2xl"
            aria-label="Zoom out"
          >
            <Minus className="w-6 h-6 text-gray-900" />
          </button>
        </div>
      )}
    </div>
  );
};

export default MapView;
